import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  FileText,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Send,
  Loader2,
  AlertCircle,
  Download,
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import FormInput from './FormInput';
import ThemeToggle from './ThemeToggle';
import { toast } from '../utils/toast';
import { generateW9Pdf } from '../utils/generateW9Pdf';
import type { PublicInviteData, W9FormData, AuditLogEntry } from '../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const TAX_CLASSIFICATIONS = [
  { value: 'individual_sole_proprietor', label: 'Individual / Sole Proprietor' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'trust_estate', label: 'Trust / Estate' },
  { value: 'llc', label: 'LLC' },
  { value: 'other', label: 'Other' },
];

type FormDataState = {
  taxpayer: {
    name: string;
    businessName: string;
    taxClassification: W9FormData['taxpayer']['taxClassification'] | '';
    llcTaxClassification: string;
    exemptPayeeCode: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  tin: {
    tinType: 'ssn' | 'ein';
    tin: string;
  };
  certification: {
    certificationAccepted: boolean;
    signature: string;
    signedDate: string;
  };
};

interface W9FormProps {
  invite: PublicInviteData;
  onSubmitted?: () => void;
}

export default function W9Form({ invite, onSubmitted }: W9FormProps) {
  const [loading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(invite.status === 'completed');
  const [completedAppId, setCompletedAppId] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [error] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [ip, setIp] = useState<string>('Unknown');
  const [openedAt] = useState(new Date());

  const [formData, setFormData] = useState<FormDataState>({
    taxpayer: {
      name: invite.signerName || '',
      businessName: invite.companyName || '',
      taxClassification: 'llc',
      llcTaxClassification: 'c',
      exemptPayeeCode: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    tin: {
      tinType: 'ein',
      tin: '',
    },
    certification: {
      certificationAccepted: false,
      signature: '',
      signedDate: new Date().toISOString().split('T')[0],
    },
  });

  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const text = await response.text();
        const data = (text && !text.trim().startsWith('<')) ? JSON.parse(text) : {};
        setIp(data.ip || 'Unknown');
      } catch {
        // Non-critical — IP used for audit log only
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateStep(step, true);
    }
  }, [formData, step]);

  const validateStep = (currentStep: number, isRealTime = false) => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!formData.taxpayer.name) newErrors.name = 'Name is required';
      if (!formData.taxpayer.taxClassification) newErrors.taxClassification = 'Tax classification is required';
      if (formData.taxpayer.taxClassification === 'llc' && !formData.taxpayer.llcTaxClassification) {
        newErrors.llcTaxClassification = 'LLC tax classification is required';
      }
      if (!formData.taxpayer.address) newErrors.address = 'Address is required';
      if (!formData.taxpayer.city) newErrors.city = 'City is required';
      if (!formData.taxpayer.state) newErrors.state = 'State is required';
      if (!formData.taxpayer.zip) newErrors.zip = 'ZIP is required';
      else if (!/^\d{5}(-\d{4})?$/.test(formData.taxpayer.zip)) newErrors.zip = 'Invalid ZIP code';
    }

    if (currentStep === 2) {
      const rawTin = formData.tin.tin.replace(/[-\s]/g, '');
      if (!rawTin) newErrors.tin = 'TIN is required';
      else if (!/^\d{9}$/.test(rawTin)) newErrors.tin = 'TIN must be 9 digits';
    }

    if (currentStep === 3) {
      if (!formData.certification.certificationAccepted) newErrors.certificationAccepted = 'You must certify the information is correct';
      if (!formData.certification.signature) newErrors.signature = 'Signature is required';
    }

    const filteredErrors = isRealTime
      ? Object.keys(newErrors).reduce((acc, key) => {
          if (touched[key]) acc[key] = newErrors[key];
          return acc;
        }, {} as Record<string, string>)
      : newErrors;

    setErrors(filteredErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    const stepFields: Record<string, boolean> = {};
    if (step === 1) {
      ['name', 'taxClassification', 'llcTaxClassification', 'address', 'city', 'state', 'zip'].forEach(f => {
        stepFields[f] = true;
      });
    } else if (step === 2) {
      ['tin'].forEach(f => { stepFields[f] = true; });
    }
    setTouched(prev => ({ ...prev, ...stepFields }));

    if (validateStep(step)) {
      setStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    setErrors({});
    setTouched({});
    setStep(s => s - 1);
    window.scrollTo(0, 0);
  };

  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async () => {
    if (!invite?.id) return;
    ['certificationAccepted', 'signature'].forEach(f => {
      setTouched(prev => ({ ...prev, [f]: true }));
    });
    if (!validateStep(3)) return;
    setSubmitting(true);
    try {
      const userAgent = navigator.userAgent;
      const rawTin = formData.tin.tin.replace(/[-\s]/g, '');

      const auditLog = [
        {
          action: 'Invite Created',
          timestamp: typeof invite.createdAt === 'string' ? invite.createdAt : new Date().toISOString(),
          details: `Invitation sent to ${invite.customerEmail} for ${invite.companyName}`,
          ip: 'System',
          userAgent: 'System',
        },
        {
          action: 'Application Opened',
          timestamp: openedAt.toISOString(),
          details: 'Application link accessed by customer',
          ip,
          userAgent,
        },
        {
          action: 'Certification Accepted',
          timestamp: new Date().toISOString(),
          details: 'Customer accepted the W-9 perjury certification',
          ip,
          userAgent,
        },
        {
          action: 'Signature Captured',
          timestamp: new Date().toISOString(),
          details: 'Customer applied digital signature',
          ip,
          userAgent,
        },
        {
          action: 'Form Completed',
          timestamp: new Date().toISOString(),
          details: 'Customer completed all steps of the W-9',
          ip,
          userAgent,
        },
        {
          action: 'Application Submitted',
          timestamp: new Date().toISOString(),
          details: `W-9 submitted for ${invite.companyName}`,
          ip,
          userAgent,
        },
      ];

      const submittedFormData: W9FormData = {
        formType: 'w9',
        taxpayer: {
          name: formData.taxpayer.name,
          businessName: formData.taxpayer.businessName,
          taxClassification: formData.taxpayer.taxClassification as W9FormData['taxpayer']['taxClassification'],
          llcTaxClassification: formData.taxpayer.llcTaxClassification,
          exemptPayeeCode: formData.taxpayer.exemptPayeeCode,
          address: formData.taxpayer.address,
          city: formData.taxpayer.city,
          state: formData.taxpayer.state,
          zip: formData.taxpayer.zip,
        },
        tin: {
          tinType: formData.tin.tinType,
          tin: rawTin,
        },
        certification: {
          certificationAccepted: formData.certification.certificationAccepted,
          signature: formData.certification.signature,
          signedDate: formData.certification.signedDate,
        },
      };

      let pdfResult: { blobUrl: string; base64: string } | null = null;
      try {
        const pdfOutput = await generateW9Pdf(
          submittedFormData,
          invite.companyName,
          {
            appId: invite.id,
            signerEmail: invite.customerEmail,
            ip,
            userAgent,
            events: auditLog,
          }
        );
        if (pdfOutput) {
          pdfResult = pdfOutput;
          setPdfBlobUrl(pdfOutput.blobUrl);
        }
      } catch (err) {
        console.error('W9 PDF generation failed:', err);
      }

      // Submit to CRM API
      const response = await fetch(`/api/public/forms/${invite.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: invite.companyName,
          customerEmail: invite.customerEmail,
          formData: submittedFormData,
          signedPdfBase64: pdfResult?.base64 ? `data:application/pdf;base64,${pdfResult.base64}` : null,
          attachments: [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit W-9');
      }

      const resJson = await response.json();
      setCompletedAppId(resJson.submissionId || invite.id);
      setCompleted(true);
      toast.success('W-9 submitted successfully!');
      if (onSubmitted) onSubmitted();
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit W-9 — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
        <div className="text-center space-y-4">
          <Loader2 className="animate-spin text-indigo-600 mx-auto" size={48} />
          <p className="text-slate-500 dark:text-slate-400 font-medium">Loading your form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors duration-300">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 p-12 rounded-[3rem] shadow-xl dark:shadow-slate-900/50 text-center space-y-6 border border-slate-100 dark:border-slate-800 transition-colors duration-300">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle size={40} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Oops!</h2>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors duration-300">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 p-12 rounded-[3rem] shadow-xl dark:shadow-slate-900/50 text-center space-y-6 border border-slate-100 dark:border-slate-800 transition-colors duration-300">
          <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={40} />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50">W-9 Submitted!</h2>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
            Thank you for completing the W-9 form. Your tax information and signed certificate have been securely recorded.
          </p>
          {invite && (
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{invite.companyName}</p>
          )}
          {pdfBlobUrl && (
            <div className="pt-2">
              <a
                href={pdfBlobUrl}
                download={`W9_${(invite?.companyName || 'Form').replace(/\s+/g, '_')}.pdf`}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
              >
                <Download size={16} /> Download Copy (PDF)
              </a>
            </div>
          )}
          {completedAppId && (
            <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">Ref: {completedAppId}</p>
          )}
        </div>
      </div>
    );
  }

  const STEPS = ['Taxpayer Information', 'TIN & Exemptions', 'Certification'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 transition-colors duration-300">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-12 relative">
          <div className="absolute right-0 top-0">
            <ThemeToggle />
          </div>
          <img
            src="https://firebasestorage.googleapis.com/v0/b/dataseeker-12378.firebasestorage.app/o/template-images%2F1770677341197_logo.png?alt=media&token=34c46eaa-ef71-403d-99e0-029e3462db45"
            alt="Armellini Logistics"
            className="h-12 mx-auto mb-8 dark:invert transition-all"
          />
          <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">W-9 / Tax Form</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2">IRS Form W-9 — Request for Taxpayer Identification Number and Certification</p>
        </header>

        {/* Progress Bar */}
        <div className="flex justify-between items-center mb-12 bg-white dark:bg-slate-900 p-4 rounded-3xl shadow-sm dark:shadow-slate-900/20 border border-slate-100 dark:border-slate-800 overflow-x-auto no-scrollbar transition-colors duration-300">
          {STEPS.map((label, idx) => {
            const s = idx + 1;
            return (
              <div key={s} className="flex items-center gap-2 shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  step >= s ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100 dark:shadow-indigo-900/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600'
                }`}>
                  {step > s ? <CheckCircle size={20} /> : s}
                </div>
                <span className={`hidden md:inline text-xs font-bold uppercase tracking-widest ${step >= s ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-600'}`}>
                  {label}
                </span>
                {s < STEPS.length && <div className="hidden md:block w-8 h-px bg-slate-200 dark:bg-slate-800 mx-2" />}
              </div>
            );
          })}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-xl dark:shadow-slate-900/50 shadow-slate-200/50 border border-slate-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 transition-colors duration-300">
          <div className="p-12">

            {/* Step 1: Taxpayer Information */}
            {step === 1 && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-3">
                  <FileText className="text-indigo-600 dark:text-indigo-400" /> Taxpayer Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Name (as shown on income tax return)"
                    value={formData.taxpayer.name}
                    error={errors.name}
                    onBlur={() => handleFieldBlur('name')}
                    onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, name: v } }))}
                    className="md:col-span-2"
                  />
                  <FormInput
                    label="Business Name / DBA (optional)"
                    value={formData.taxpayer.businessName}
                    onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, businessName: v } }))}
                    className="md:col-span-2"
                  />

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                      Federal Tax Classification
                    </label>
                    <select
                      value={formData.taxpayer.taxClassification}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        taxpayer: {
                          ...prev.taxpayer,
                          taxClassification: e.target.value as W9FormData['taxpayer']['taxClassification'],
                          llcTaxClassification: e.target.value !== 'llc' ? '' : prev.taxpayer.llcTaxClassification,
                        }
                      }))}
                      onBlur={() => handleFieldBlur('taxClassification')}
                      className={`w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none ${
                        errors.taxClassification ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800'
                      }`}
                    >
                      <option value="">Select classification...</option>
                      {TAX_CLASSIFICATIONS.map(tc => (
                        <option key={tc.value} value={tc.value}>{tc.label}</option>
                      ))}
                    </select>
                    {errors.taxClassification && (
                      <p className="text-[10px] font-bold text-red-500 ml-1">{errors.taxClassification}</p>
                    )}
                  </div>

                  {formData.taxpayer.taxClassification === 'llc' && (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                        LLC Tax Classification
                      </label>
                      <select
                        value={formData.taxpayer.llcTaxClassification}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          taxpayer: { ...prev.taxpayer, llcTaxClassification: e.target.value }
                        }))}
                        onBlur={() => handleFieldBlur('llcTaxClassification')}
                        className={`w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none ${
                          errors.llcTaxClassification ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800'
                        }`}
                      >
                        <option value="">Select...</option>
                        <option value="C">C — Taxed as C Corporation</option>
                        <option value="S">S — Taxed as S Corporation</option>
                        <option value="P">P — Taxed as Partnership</option>
                      </select>
                      {errors.llcTaxClassification && (
                        <p className="text-[10px] font-bold text-red-500 ml-1">{errors.llcTaxClassification}</p>
                      )}
                    </div>
                  )}

                  <FormInput
                    label="Address (number, street, apt/suite)"
                    value={formData.taxpayer.address}
                    error={errors.address}
                    onBlur={() => handleFieldBlur('address')}
                    onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, address: v } }))}
                    className="md:col-span-2"
                  />
                  <FormInput
                    label="City"
                    value={formData.taxpayer.city}
                    error={errors.city}
                    onBlur={() => handleFieldBlur('city')}
                    onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, city: v } }))}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">State</label>
                      <select
                        value={formData.taxpayer.state}
                        onChange={e => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, state: e.target.value } }))}
                        onBlur={() => handleFieldBlur('state')}
                        className={`w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none ${
                          errors.state ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' : 'border-slate-100 dark:border-slate-800'
                        }`}
                      >
                        <option value="">State...</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.state && <p className="text-[10px] font-bold text-red-500 ml-1">{errors.state}</p>}
                    </div>
                    <FormInput
                      label="ZIP"
                      value={formData.taxpayer.zip}
                      error={errors.zip}
                      onBlur={() => handleFieldBlur('zip')}
                      onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, zip: v } }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: TIN & Exemptions */}
            {step === 2 && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-3">
                  <ShieldCheck className="text-indigo-600 dark:text-indigo-400" /> TIN &amp; Exemptions
                </h2>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">TIN Type</label>
                    <div className="flex gap-6">
                      {(['ssn', 'ein'] as const).map(type => (
                        <label key={type} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="radio"
                            name="tinType"
                            value={type}
                            checked={formData.tin.tinType === type}
                            onChange={() => setFormData(prev => ({ ...prev, tin: { ...prev.tin, tinType: type } }))}
                            className="w-5 h-5 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                          />
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                            {type === 'ssn' ? 'Social Security Number (SSN)' : 'Employer Identification Number (EIN)'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <FormInput
                    label={formData.tin.tinType === 'ssn' ? 'Social Security Number (e.g. 123-45-6789)' : 'Employer Identification Number (e.g. 12-3456789)'}
                    value={formData.tin.tin}
                    error={errors.tin}
                    onBlur={() => handleFieldBlur('tin')}
                    onChange={v => setFormData(prev => ({ ...prev, tin: { ...prev.tin, tin: v } }))}
                  />
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Enter your 9-digit TIN. Dashes are optional. The number will be stored securely.
                  </p>

                  <FormInput
                    label="Exempt Payee Code (optional)"
                    value={formData.taxpayer.exemptPayeeCode}
                    onChange={v => setFormData(prev => ({ ...prev, taxpayer: { ...prev.taxpayer, exemptPayeeCode: v } }))}
                  />
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Leave blank if you are not exempt from backup withholding.
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Certification */}
            {step === 3 && (
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-3">
                  <ShieldCheck className="text-indigo-600 dark:text-indigo-400" /> Certification
                </h2>

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 space-y-3 border border-slate-100 dark:border-slate-700">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Under penalties of perjury, I certify that:</p>
                  <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-decimal list-inside leading-relaxed">
                    <li>The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me);</li>
                    <li>I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding;</li>
                    <li>I am a U.S. citizen or other U.S. person; and</li>
                    <li>The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</li>
                  </ol>
                </div>

                <label className="flex items-start gap-4 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.certification.certificationAccepted}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        certification: { ...prev.certification, certificationAccepted: e.target.checked }
                      }));
                      handleFieldBlur('certificationAccepted');
                    }}
                    className="w-5 h-5 mt-0.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 shrink-0"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                    I certify that the information provided above is correct and that I am authorized to sign this W-9.
                  </span>
                </label>
                {errors.certificationAccepted && (
                  <p className="text-[10px] font-bold text-red-500 ml-1">{errors.certificationAccepted}</p>
                )}

                <div className="space-y-3">
                  <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Signature</label>
                  <SignaturePad
                    label="Signature"
                    onSave={sig => {
                      setFormData(prev => ({
                        ...prev,
                        certification: { ...prev.certification, signature: sig }
                      }));
                      handleFieldBlur('signature');
                    }}
                  />
                  {errors.signature && (
                    <p className="text-[10px] font-bold text-red-500 ml-1">{errors.signature}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Date</label>
                    <div className="px-5 py-4 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-2xl border border-slate-100 dark:border-slate-800 text-sm font-medium">
                      {formData.certification.signedDate}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-12 pt-8 border-t border-slate-100 dark:border-slate-800">
              {step > 1 ? (
                <button
                  onClick={handlePrev}
                  disabled={submitting}
                  className="flex items-center gap-2 px-8 py-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-2xl font-bold transition-all disabled:opacity-50"
                >
                  <ChevronLeft size={20} /> Previous
                </button>
              ) : (
                <div />
              )}

              {step < 3 && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-indigo-100 dark:shadow-indigo-900/20"
                >
                  Next <ChevronRight size={20} />
                </button>
              )}

              {step === 3 && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-white px-12 py-4 rounded-2xl font-bold transition-all shadow-xl disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                  Submit W-9
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
